import {
	Dispatch,
	ReadonlyRefObject,
	useCallback
} from "react";
import { EMPTY_ARRAY, clamp } from "client/Misc/utils";
import { EmailListItemModel } from "client/Api/Email/email";
import { EmailActionModel } from "../Actions/selectionActions";
import { ItemHandle } from "./Item/Item";
import {
	ItemSelectionKind,
	SelectableItemOptions,
	useSynchronizedRef
} from "client/Hooks";
import { SelectMode } from "client/Misc/commonTypes";
import { EmailListItemWithMailbox } from "./ListView";
import { ScrollableVirtualListHandle } from "client/Components";

type KeyboardNavigationHandlers = {
	navigateToResultsHandler: () => void;
	keyboardNextItemHandler: (index: number, isShift: boolean) => void;
	keyboardPreviousItemHandler: (index: number, isShift: boolean) => void;
	keyboardNextPageItemHandler: (index: number, isShift: boolean) => void;
	keyboardPreviousPageItemHandler: (index: number, isShift: boolean) => void;
	keyboardFirstItemHandler: (index: number, isShift: boolean) => void;
	keyboardLastItemHandler: (index: number, isShift: boolean) => void;
	keyboardDeleteHandler: (index: number) => void;
};

export function useKeyboardNavigation(
	items: readonly EmailListItemWithMailbox[],
	selectMode: SelectMode,
	itemRefs: ReadonlyRefObject<Map<string, ItemHandle>>,
	virtualListRef: ReadonlyRefObject<ScrollableVirtualListHandle<EmailListItemWithMailbox> | null>,
	selectionFor: (item: EmailListItemWithMailbox) => SelectableItemOptions,
	setSelectedItems: Dispatch<readonly EmailActionModel[]>,
	deleteSelectedEmails: () => void,
	deleteEmail: (email: EmailListItemModel) => void,
	openEmail: (email: EmailListItemModel) => void
): KeyboardNavigationHandlers {
	const itemsRef: ReadonlyRefObject<readonly EmailListItemWithMailbox[]> = useSynchronizedRef(items);

	const keyboardNavigationHandler = useCallback(
		(
			targetIndex: number,
			isShift: boolean,
			index: number
		): void => {
			if (selectMode !== SelectMode.Disabled) {
				return;
			}

			const items = itemsRef.current;

			targetIndex = clamp(
				targetIndex,
				0,
				items.length - 1
			);

			const itemToSelect: EmailListItemWithMailbox | undefined = items[targetIndex];

			if (!itemToSelect) {
				return;
			}

			virtualListRef.current
				?.scrollIntoView(itemToSelect);

			setTimeout(
				(): void => {
					// It is likely the item was not rendered yet (especially with Page up/down navigation)
					// so we don't know a ref at this point in time. So we queue the focusing of the item
					// to be shortly in the future
					itemRefs.current
						.get(itemToSelect.emailId.id)
						?.focus();
				},
				50
			);

			if (isShift) {
				const currentItem: EmailListItemWithMailbox | undefined = items[index];

				if (currentItem) {
					const itemSelectable: SelectableItemOptions = selectionFor(currentItem);
					if (itemSelectable && !itemSelectable.isSelected) {
						itemSelectable.onSelect(ItemSelectionKind.Append);
					}
				}

				const selectable: SelectableItemOptions = selectionFor(itemToSelect);
				if (selectable) {
					selectable.onSelect(ItemSelectionKind.RangeFromPrevious);
				}

			} else {
				setSelectedItems(EMPTY_ARRAY);
				openEmail(itemToSelect);
			}
		},
		[
			itemRefs,
			itemsRef,
			selectionFor,
			selectMode,
			setSelectedItems,
			virtualListRef,
			openEmail
		]
	);

	const navigateToResultsHandler = useCallback(
		(): void => {
			keyboardNavigationHandler(0, false, 0);
		},
		[keyboardNavigationHandler]
	);

	const keyboardNextItemHandler = useCallback(
		(index: number, isShift: boolean): void => {
			keyboardNavigationHandler(index + 1, isShift, index);
		},
		[keyboardNavigationHandler]
	);

	const keyboardPreviousItemHandler = useCallback(
		(index: number, isShift: boolean): void => {
			keyboardNavigationHandler(index - 1, isShift, index);
		},
		[keyboardNavigationHandler]
	);

	const keyboardNextPageItemHandler = useCallback(
		(index: number, isShift: boolean): void => {
			keyboardNavigationHandler(index + 10, isShift, index);
		},
		[keyboardNavigationHandler]
	);

	const keyboardPreviousPageItemHandler = useCallback(
		(index: number, isShift: boolean): void => {
			keyboardNavigationHandler(index - 10, isShift, index);
		},
		[keyboardNavigationHandler]
	);

	const keyboardFirstItemHandler = useCallback(
		(index: number, isShift: boolean): void => {
			keyboardNavigationHandler(0, isShift, index);
		},
		[keyboardNavigationHandler]
	);

	const keyboardLastItemHandler = useCallback(
		(index: number, isShift: boolean): void => {
			keyboardNavigationHandler(
				items.length - 1,
				isShift,
				index
			);
		},
		[
			items.length,
			keyboardNavigationHandler
		]
	);

	const keyboardDeleteHandler = useCallback(
		(index: number): void => {
			const itemToBeDeleted: EmailListItemWithMailbox | undefined = itemsRef.current[index];

			if (!itemToBeDeleted) {
				return;
			}

			const itemSelectable: SelectableItemOptions = selectionFor(itemToBeDeleted);
			if (itemSelectable && itemSelectable.isSelected) {
				deleteSelectedEmails();
			} else {
				deleteEmail(itemToBeDeleted);
			}
		},
		[
			deleteEmail,
			deleteSelectedEmails,
			itemsRef,
			selectionFor
		]
	);

	return {
		navigateToResultsHandler,
		keyboardNextItemHandler,
		keyboardPreviousItemHandler,
		keyboardNextPageItemHandler,
		keyboardPreviousPageItemHandler,
		keyboardFirstItemHandler,
		keyboardLastItemHandler,
		keyboardDeleteHandler
	};
}
